import React, { useState } from "react";
import { AlertCircle, ChevronDown, ChevronRight } from "lucide-react";
import { formatDisplayValue } from "../../utils/runAgentResponse";
import EditableTable from "./EditableTable";
import "./Playground.css";

const DynamicContentRenderer = ({ contentData, onFieldChange, onFieldCommit, saving, savingFieldId, onTableChange, onTableCommit, onAction }) => {
  const [expandedSections, setExpandedSections] = useState({});
  const [exceptionsExpanded, setExceptionsExpanded] = useState(true); // Default to expanded

  const renderField = (field) => {
    const { id, type, label, value, placeholder, options, required, editable, icon } = field;

    const handleChange = (e) => {
      const newValue = e.target.value;
      if (onFieldChange) {
        onFieldChange(id, newValue);
      }
    };

    const fieldValue = formatDisplayValue(value);
    const commitValue = (val) => {
      if (!editable) return;
      if (onFieldCommit) {
        onFieldCommit(id, val);
      }
    };

    return (
      <div key={id} className="dynamic-field">
        <label className="dynamic-field-label">
          {label}
          {required && <span className="required-asterisk">*</span>}
          {saving && savingFieldId === id && (
            <span className="field-saving-indicator">Saving...</span>
          )}
        </label>
        <div className="dynamic-field-input-wrapper">
          {type === "select" ? (
            <select
              className="form-control dynamic-field-input"
              value={fieldValue}
              onChange={handleChange}
              onBlur={(e) => commitValue(e.target.value)}
              disabled={!editable}
            >
              <option value="">{placeholder || "Select..."}</option>
              {options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : type === "textarea" ? (
            <textarea
              className="form-control dynamic-field-input"
              value={fieldValue}
              onChange={handleChange}
              onBlur={(e) => commitValue(e.target.value)}
              placeholder={placeholder}
              disabled={!editable}
              rows={3}
            />
          ) : (
            <input
              type={type || "text"}
              className="form-control dynamic-field-input"
              value={fieldValue}
              onChange={handleChange}
              onBlur={(e) => commitValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitValue(e.currentTarget.value);
                  e.currentTarget.blur();
                }
              }}
              placeholder={placeholder}
              disabled={!editable}
            />
          )}
        </div>
      </div>
    );
  };

  const renderException = (exception, index) => {
    // Handle both string and object formats
    let exceptionData;
    if (typeof exception === 'string') {
      exceptionData = { message: exception, id: `exception-${index}` };
    } else {
      exceptionData = exception;
    }

    const { id, message, status, isResolved, actions } = exceptionData;
    // Check both status and isResolved field
    const resolved = isResolved === true || status === "resolved";

    return (
      <div
        key={id || index}
        className={`exception-item exception-error ${resolved ? "exception-resolved" : ""}`}
      >
        <div className="exception-content">
          <div className="exception-icon">
            <AlertCircle size={18} />
          </div>
          <div className="exception-message">{formatDisplayValue(message)}</div>
        </div>
        {actions && actions.length > 0 && (
          <div className="exception-actions">
            {actions.map((action, actionIndex) => (
              <button
                key={actionIndex}
                className={`btn-exception-action ${action.type || "default"}`}
                onClick={() => {
                  if (onAction) {
                    onAction(action.action, id, action);
                  }
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const renderSection = (section) => {
    const { id, title, subtitle, type, content, fields, table, exceptions } = section;
    const isExpanded = expandedSections[id] !== false; // Default to expanded

    return (
      <div key={id} className="dynamic-section">
        {title && (
          <div 
            className="dynamic-section-header"
            onClick={() => toggleSection(id)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {isExpanded ? (
              <ChevronDown size={18} className="section-chevron" />
            ) : (
              <ChevronRight size={18} className="section-chevron" />
            )}
            <h6 className="dynamic-section-title">{title}</h6>
          </div>
        )}
        {subtitle && <p className="dynamic-section-subtitle">{subtitle}</p>}
        
        {isExpanded && (
          <div className="dynamic-section-content">
            {type === "form" && fields && (
              <div className="dynamic-form">
                {fields.map((field) => renderField(field))}
              </div>
            )}

            {type === "table" && table && (
              <div className="dynamic-table-container">
                <EditableTable
                  data={table.data || []}
                  columns={table.columns || []}
                  onDataChange={(newData) => {
                    if (onTableChange) onTableChange(id, newData);
                  }}
                  onCommit={(newData) => {
                    if (onTableCommit) onTableCommit(id, newData);
                  }}
                />
              </div>
            )}

            {type === "exceptions" && (() => {
              // Normalize exceptions - handle both array and string formats
              let normalizedExceptions = [];
              if (exceptions) {
                if (Array.isArray(exceptions)) {
                  normalizedExceptions = exceptions;
                } else if (typeof exceptions === 'string') {
                  normalizedExceptions = [exceptions];
                }
              }

              if (normalizedExceptions.length === 0) {
                return null;
              }

              return (
                <div className="dynamic-exceptions">
                  {normalizedExceptions.map((exception, index) => renderException(exception, index))}
                </div>
              );
            })()}

            {type === "custom" && content && (
              <div className="dynamic-custom-content">{content}</div>
            )}

            {type === "mixed" && (
              <>
                {(() => {
                  // Normalize exceptions - handle both array and string formats
                  let normalizedExceptions = [];
                  if (exceptions) {
                    if (Array.isArray(exceptions)) {
                      normalizedExceptions = exceptions;
                    } else if (typeof exceptions === 'string') {
                      normalizedExceptions = [exceptions];
                    }
                  }

                  if (normalizedExceptions.length === 0) {
                    return null;
                  }

                  return (
                    <div className="dynamic-exceptions">
                      {normalizedExceptions.map((exception, index) => renderException(exception, index))}
                    </div>
                  );
                })()}
                {fields && fields.length > 0 && (
                  <div className="dynamic-form">
                    {fields.map((field) => renderField(field))}
                  </div>
                )}
                {table && (
                  <div className="dynamic-table-container">
                    <EditableTable
                      data={table.data || []}
                      columns={table.columns || []}
                      onDataChange={(newData) => {
                        if (onTableChange) onTableChange(id, newData);
                      }}
                      onCommit={(newData) => {
                        if (onTableCommit) onTableCommit(id, newData);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!contentData || !Array.isArray(contentData) || contentData.length === 0) {
    return (
      <div className="dynamic-content-empty">
        <p>No content data available</p>
      </div>
    );
  }

  // Separate exceptions from other content
  const exceptionsSection = contentData.find(item => item.type === "exceptions");
  const otherContent = contentData.filter(item => item.type !== "exceptions");

  return (
    <>
      {/* Exceptions Section with its own wrapper */}
      {exceptionsSection && (() => {
        // Normalize exceptions - handle both array and string formats
        let normalizedExceptions = [];
        if (exceptionsSection.exceptions) {
          if (Array.isArray(exceptionsSection.exceptions)) {
            normalizedExceptions = exceptionsSection.exceptions;
          } else if (typeof exceptionsSection.exceptions === 'string') {
            normalizedExceptions = [exceptionsSection.exceptions];
          }
        }

        if (normalizedExceptions.length === 0) {
          return null;
        }

        return (
          <div className="exceptions-wrapper">
            <div 
              className={`exceptions-header ${!exceptionsExpanded ? 'exceptions-header-collapsed' : ''}`}
              onClick={() => setExceptionsExpanded(!exceptionsExpanded)}
              style={{ cursor: 'pointer' }}
            >
              <div className="exceptions-header-content">
                {exceptionsExpanded ? (
                  <ChevronDown size={20} className="exception-toggle-icon" />
                ) : (
                  <ChevronRight size={20} className="exception-toggle-icon" />
                )}
                <h2 className="exceptions-title">{exceptionsSection.title || "Exceptions"}</h2>
                <span className="exceptions-count">({normalizedExceptions.length})</span>
              </div>
            </div>
            {exceptionsExpanded && (
              <div className="exceptions-content">
                <div className="dynamic-exceptions">
                  {normalizedExceptions.map((exception, index) => renderException(exception, index))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Other Content with main wrapper */}
      {otherContent.length > 0 && (
        <div className="main-content-wrapper">
          <div className="dynamic-content-header">
            <h2 className="dynamic-content-title">Extracted & Enriched Invoice Data</h2>
          </div>
          <div className="dynamic-content-renderer">
            {otherContent.map((item, index) => {
              // If item has id, title, or type, it's a section - render with accordion
              if (item.id || item.title || item.type) {
                return renderSection(item);
              }
              // If item is just fields without a parent section, render fields directly
              if (item.fields && Array.isArray(item.fields)) {
                return (
                  <div key={item.id || `fields-section-${index}`} className="dynamic-form">
                    {item.fields.map((field) => renderField(field))}
                  </div>
                );
              }
              // Otherwise render as-is (don't add anything extra)
              return null;
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default DynamicContentRenderer;

